'use client'
import { useContext, useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CUSTOMER_LEVEL } from "@prisma/client";
import { MSProductWithFullDetailWithVaraintPreset } from "@/util/query/product-query";
import { useLayoutStyle } from "@/lib/providers/layout-style-provider";
import { useProductLayout } from "@/lib/providers/product-layout-provider";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import CollectionListItem, { ProductCardConfig } from "../collections/collection-list-item";
import "./product-related.css";

interface ProductRelatedItemCarouselProps {
    product: MSProductWithFullDetailWithVaraintPreset[];
    level: CUSTOMER_LEVEL;
    shopCurrency: string;
}

const ProductRelatedItemCarousel = (props: ProductRelatedItemCarouselProps) => {
    const { product, level, shopCurrency } = props;
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: true });
        
    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(true);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setPrevDisabled(!emblaApi.canScrollPrev());
        setNextDisabled(!emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
    }, [emblaApi, onSelect]);

    const scrollNext = () => {
        if (emblaApi) emblaApi.scrollNext();
    };
    const scrollPrev = () => {
        if (emblaApi) emblaApi.scrollPrev();
    };

    const productLayout = useProductLayout();
    const layoutStyle = useLayoutStyle();
    const colorStyle = useContext(ColorStyleContext);

    const type = productLayout?.productCardType ?? "TYPE_1";
    const maxItem = productLayout.productColumns
    const isGradientCard = type === "TYPE_2";
    const resolvedMobileColumn = productLayout.mobileColumn ?? 2;
    const useTightMobileGap = isGradientCard && resolvedMobileColumn === 3;

    return (
        <div id="product-related-carousel-container" className="w-full relative">
            <div id="product-related-carousel-embla" className="embla" ref={emblaRef}>
                <div
                    id="product-related-carousel-embla-container"
                    className={`embla__container product-related-carousel-embla${useTightMobileGap ? " product-related-gap-tight" : ""}`}
                    style={{ display: "flex", ...(useTightMobileGap ? { "--mobile-gap": "8px" } : {}) } as React.CSSProperties}
                >
                    {product.map((product, index) => (
                        (product && product.productVariant && product.productVariant.length > 0) && (
                            <div
                                id={`product-related-carousel-item-${product.id}`}
                                key={product.id}
                                className={`${maxItem === 6 ? "product-related-6" : "product-related"}${isGradientCard ? " product-related-mobile-custom" : ""} flex flex-col gap-5 flex-shrink-0`}
                                style={isGradientCard ? ({ "--mobile-columns": resolvedMobileColumn } as React.CSSProperties) : undefined}
                            >
                                <CollectionListItem
                                    product={product}
                                    key={product.id}
                                    href={`/products/${product.handle}`}
                                    level={level}
                                    priorityImage={index < 2}
                                    shopCurrency={shopCurrency}
                                    cardType={type == "TYPE_1" ? ProductCardConfig.DEFAULT : 
                                            type == "TYPE_2" ? ProductCardConfig.GRADIENT : 
                                            type == "TYPE_3" ? ProductCardConfig.BUTTON_BESIDE_PRICE :
                                            type == "TYPE_5" ? ProductCardConfig.BORDER_GRADIENT_SHORT_DESC :
                                            ProductCardConfig.DEFAULT}
                                />
                            </div>
                        )))}
                </div>
            </div>
            {product.length > maxItem && (
                productLayout.arrowPosition == "SIDE" ?
                <><button
                    aria-label="Previous"
                    className="
                        embla__button embla__button--prev inline-flex items-center justify-center whitespace-nowrap 
                        ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 
                        focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 
                        text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden dynamic-bg dynamic-color 
                        dynamic-border absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 shadow"
                    style={
                        {
                            "--dynamic-bg": "#ffffff",
                            "--dynamic-hover-bg": "#ffffff",
                            "--dynamic-border": "#B3B3B3",
                            "--dynamic-hover-border": "#FFC20E",
                            "--dynamic-color": "#5B595A",
                            "--dynamic-hover-color": "#5B595A",
                            "--dynamic-focus-color": "#5B595A",
                            "--dynamic-hover-focus-color": "#5B595A",
                        } as React.CSSProperties
                    }
                    onClick={scrollPrev}
                    disabled={prevDisabled}
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                    aria-label="Next"
                    className="
                        embla__button embla__button--next inline-flex items-center justify-center whitespace-nowrap 
                        ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 
                        focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 
                        text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden dynamic-bg dynamic-color 
                        dynamic-border absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 shadow"
                    style={
                        {
                            "--dynamic-bg": "#ffffff",
                            "--dynamic-hover-bg": "#ffffff",
                            "--dynamic-border": "#B3B3B3",
                            "--dynamic-hover-border": "#FFC20E",
                            "--dynamic-color": "#5B595A",
                            "--dynamic-hover-color": "#5B595A",
                            "--dynamic-focus-color": "#5B595A",
                            "--dynamic-hover-focus-color": "#5B595A",
                        } as React.CSSProperties
                    }
                    onClick={scrollNext}
                    disabled={nextDisabled}
                >
                    <ChevronRight className="h-5 w-5" />
                </button></> : (
                <div id="product-related-carousel-prev-next" className="w-full flex justify-end mt-[20px]">
                    <button
                        aria-label="Previous" 
                        className={`
                            embla__button embla__button--prev inline-flex items-center justify-center 
                            whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                            focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                            disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden 
                            dynamic-bg dynamic-color dynamic-border relative transform left-0 top-0 translate-y-0 mx-1`}
                        style={
                            {
                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                            } as React.CSSProperties
                        }
                        onClick={scrollPrev}
                        disabled={prevDisabled}
                    >
                        <ChevronLeft 
                            className="h-5 w-5"
                        />
                    </button>
                    <button
                        aria-label="Next"
                        className={`
                            embla__button embla__button--next inline-flex items-center justify-center 
                            whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                            focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                            disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full -right-12 max-md:hidden 
                            dynamic-bg dynamic-color dynamic-border relative transform left-0 top-0 translate-y-0 mx-1`}
                        style={
                            {
                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                            } as React.CSSProperties
                        }
                        onClick={scrollNext}
                        disabled={nextDisabled}
                    >
                        <ChevronRight 
                            className="h-5 w-5"
                        />
                    </button>
                </div>))}
        </div>
    )
}

export default ProductRelatedItemCarousel;